<template>
  <div>
    <div style="margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center;">
      <div>
        <a-button type="primary" @click="handleAddProvider">
          <template #icon><PlusOutlined /></template>
          添加服务商
        </a-button>
      </div>
      <div style="color: #999; font-size: 12px;">
        服务商配置支持完全后台管理，添加新服务商无需修改代码
      </div>
    </div>

    <a-table
      :columns="columns"
      :data-source="providerList"
      :loading="loading"
      :pagination="pagination"
      row-key="id"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'provider_name'">
          <div>
            <strong>{{ record.provider_name }}</strong>
            <div style="color: #999; font-size: 12px;">{{ record.provider_code }}</div>
          </div>
        </template>
        <template v-else-if="column.key === 'api_type'">
          <a-tag :color="getApiTypeColor(record.api_type)">
            {{ getApiTypeName(record.api_type) }}
          </a-tag>
        </template>
        <template v-else-if="column.key === 'api_url'">
          <span v-if="record.api_url" style="font-family: monospace; font-size: 12px;">
            {{ record.api_url }}
          </span>
          <span v-else style="color: #999;">-</span>
        </template>
        <template v-else-if="column.key === 'api_key'">
          <a-tag v-if="record.has_api_key" color="green">已配置</a-tag>
          <a-tag v-else color="default">未配置</a-tag>
        </template>
        <template v-else-if="column.key === 'is_enabled'">
          <a-switch
            :checked="record.is_enabled === 1"
            @change="(checked) => handleToggleEnabled(record, checked)"
          />
        </template>
        <template v-else-if="column.key === 'action'">
          <a-button 
            type="link" 
            size="small" 
            @click="handleTestProvider(record)" 
            :loading="testingId === record.id"
          >
            测试
          </a-button>
          <a-button type="link" size="small" @click="handleEditProvider(record)">
            编辑
          </a-button>
          <a-popconfirm 
            title="确定删除该服务商吗？" 
            @confirm="handleDeleteProvider(record.id)"
          >
            <a-button type="link" size="small" danger>删除</a-button>
          </a-popconfirm>
        </template>
      </template>
    </a-table>

    <!-- 添加/编辑服务商弹窗 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEdit ? '编辑服务商' : '添加服务商'"
      width="700px"
      @ok="handleSubmit"
      @cancel="handleCancel"
      :confirm-loading="submitting"
    >
      <a-form 
        ref="formRef"
        :model="formData" 
        :rules="formRules"
        :label-col="{ span: 6 }" 
        :wrapper-col="{ span: 18 }"
      >
        <a-form-item label="服务商代码" name="provider_code" required>
          <a-input
            v-model:value="formData.provider_code"
            placeholder="唯一标识，如：deepseek, openrouter"
            :disabled="isEdit"
          />
          <div style="color: #999; font-size: 12px; margin-top: 4px;">
            用于关联模型，创建后不可修改
          </div>
        </a-form-item>
        
        <a-form-item label="服务商名称" name="provider_name" required>
          <a-input
            v-model:value="formData.provider_name"
            placeholder="显示名称，如：DeepSeek"
          />
        </a-form-item>
        
        <a-form-item label="API类型" name="api_type" required>
          <a-radio-group v-model:value="formData.api_type">
            <a-radio value="openai_compatible">OpenAI 兼容</a-radio>
            <a-radio value="google_gemini">Google Gemini</a-radio>
            <a-radio value="custom">自定义</a-radio>
          </a-radio-group>
          <div style="color: #999; font-size: 12px; margin-top: 4px;">
            <span v-if="formData.api_type === 'openai_compatible'">
              适用于 OpenRouter, DeepSeek, 豆包, CloudAce, 通义千问等
            </span>
            <span v-else-if="formData.api_type === 'google_gemini'">
              适用于 Google AI Studio, Vertex AI
            </span>
            <span v-else>
              需要自定义实现
            </span>
          </div>
        </a-form-item>
        
        <a-form-item 
          v-if="formData.api_type === 'openai_compatible'" 
          label="API 地址" 
          name="api_url"
        >
          <a-input
            v-model:value="formData.api_url"
            placeholder="如：https://api.deepseek.com"
          />
        </a-form-item>
        
        <a-form-item 
          v-if="formData.api_type === 'openai_compatible'" 
          label="API 版本路径" 
          name="api_version"
        >
          <a-select v-model:value="formData.api_version" style="width: 200px;">
            <a-select-option value="v1">v1 (标准 OpenAI)</a-select-option>
            <a-select-option value="api/v3">api/v3 (豆包)</a-select-option>
            <a-select-option value="api/v1">api/v1</a-select-option>
          </a-select>
          <div style="color: #999; font-size: 12px; margin-top: 4px;">
            大多数服务商使用 v1，豆包使用 api/v3
          </div>
        </a-form-item>
        
        <a-form-item label="API Key" name="api_key">
          <a-input-password
            v-model:value="formData.api_key"
            :placeholder="isEdit ? '留空则不修改，输入新密钥则更新' : '请输入 API Key'"
          />
          <div v-if="isEdit" style="color: #999; font-size: 12px; margin-top: 4px;">
            <span v-if="editingRecord?.has_api_key && !formData.api_key">
              <a-tag color="green" size="small">已配置</a-tag>
              当前已配置密钥，留空表示不修改，输入新密钥则更新
            </span>
            <span v-else-if="formData.api_key">
              <a-tag color="orange" size="small">将更新</a-tag>
              已输入新密钥，保存后将更新
            </span>
            <span v-else-if="!editingRecord?.has_api_key">
              <a-tag color="default" size="small">未配置</a-tag>
              当前未配置密钥，请输入 API Key
            </span>
          </div>
        </a-form-item>
        
        <!-- Vertex AI 特殊配置 -->
        <template v-if="formData.api_type === 'google_gemini' && formData.provider_code === 'vertex_ai'">
          <a-form-item label="项目ID" name="project_id">
            <a-input
              v-model:value="formData.extra_config.project_id"
              placeholder="Google Cloud 项目ID"
            />
          </a-form-item>
          <a-form-item label="区域" name="region">
            <a-select v-model:value="formData.extra_config.region" style="width: 200px;">
              <a-select-option value="us-central1">us-central1</a-select-option>
              <a-select-option value="us-east1">us-east1</a-select-option>
              <a-select-option value="europe-west1">europe-west1</a-select-option>
              <a-select-option value="asia-northeast1">asia-northeast1</a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="PayGo 模式" name="paygo_mode">
            <a-switch v-model:checked="formData.extra_config.paygo_mode" />
            <span style="margin-left: 8px; color: #888; font-size: 12px;">
              开启后价格更贵但更稳定，仅在限流严重时使用
            </span>
          </a-form-item>
        </template>
        
        <a-form-item label="默认模型" name="default_model">
          <a-select
            v-if="availableModels.length"
            v-model:value="formData.default_model"
            placeholder="请选择模型ID"
            show-search
            allow-clear
            :filter-option="filterModelOption"
            :loading="loadingModels"
          >
            <a-select-option
              v-for="model in availableModels"
              :key="model.id"
              :value="model.id"
            >
              {{ model.name || model.id }}
              <span v-if="model.description" style="color: #999; font-size: 12px;">
                - {{ model.description }}
              </span>
            </a-select-option>
          </a-select>
          <a-input
            v-else
            v-model:value="formData.default_model"
            placeholder="请输入模型ID，或点击下方按钮获取模型列表"
          />
          <div v-if="formData.api_type === 'openai_compatible' && formData.api_url && formData.api_key" style="margin-top: 4px;">
            <a-button 
              type="link" 
              size="small" 
              @click="handleFetchModels" 
              :loading="loadingModels"
            >
              获取模型列表
            </a-button>
            <span v-if="availableModels.length" style="color: #999; font-size: 12px; margin-left: 8px;">
              已加载 {{ availableModels.length }} 个模型
            </span>
          </div>
        </a-form-item>
        
        <a-form-item label="测试模型ID" name="test_model_id">
          <a-select
            v-if="availableModels.length"
            v-model:value="formData.test_model_id"
            placeholder="请选择模型ID"
            show-search
            allow-clear
            :filter-option="filterModelOption"
            :loading="loadingModels"
          >
            <a-select-option
              v-for="model in availableModels"
              :key="model.id"
              :value="model.id"
            >
              {{ model.name || model.id }}
              <span v-if="model.description" style="color: #999; font-size: 12px;">
                - {{ model.description }}
              </span>
            </a-select-option>
          </a-select>
          <a-input
            v-else
            v-model:value="formData.test_model_id"
            placeholder="请输入模型ID，或点击上方按钮获取模型列表"
          />
        </a-form-item>
        
        <a-form-item label="排序" name="sort_order">
          <a-input-number
            v-model:value="formData.sort_order"
            :min="0"
            :max="999"
            placeholder="越小越靠前"
            style="width: 120px;"
          />
        </a-form-item>
        
        <a-form-item label="是否启用" name="is_enabled">
          <a-switch v-model:checked="formData.is_enabled" />
        </a-form-item>
        
        <a-form-item label="描述" name="description">
          <a-textarea
            v-model:value="formData.description"
            placeholder="服务商描述说明"
            :rows="2"
          />
        </a-form-item>
      </a-form>
      
      <!-- 测试按钮 -->
      <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #f0f0f0;">
        <a-button 
          @click="handleTestNewConfig" 
          :loading="testingNew"
          :disabled="!canTest"
        >
          测试配置
        </a-button>
        <span v-if="testResult" style="margin-left: 16px;">
          <a-tag v-if="testResult.success" color="green">
            测试成功 ({{ testResult.duration_ms }}ms)
          </a-tag>
          <a-tag v-else color="red">
            测试失败
          </a-tag>
        </span>
      </div>
    </a-modal>

    <!-- 测试结果弹窗 -->
    <a-modal
      v-model:open="testResultModalVisible"
      title="测试结果"
      width="700px"
      :footer="null"
    >
      <a-descriptions :column="1" bordered size="small">
        <a-descriptions-item label="服务商">{{ testResultData.provider_name }}</a-descriptions-item>
        <a-descriptions-item label="模型">{{ testResultData.model_id }}</a-descriptions-item>
        <a-descriptions-item label="耗时">{{ testResultData.duration_ms }} ms</a-descriptions-item>
        <a-descriptions-item v-if="testResultData.usage" label="Token 使用">
          输入: {{ testResultData.usage.promptTokens }}, 输出: {{ testResultData.usage.completionTokens }}
        </a-descriptions-item>
      </a-descriptions>
      <div style="margin-top: 16px;">
        <div style="font-weight: bold; margin-bottom: 8px;">响应内容：</div>
        <div style="background: #f5f5f5; padding: 12px; border-radius: 4px; max-height: 300px; overflow-y: auto;">
          <pre style="margin: 0; white-space: pre-wrap; word-wrap: break-word;">{{ testResultData.response }}</pre>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { PlusOutlined } from '@ant-design/icons-vue';
import {
  getProviderList,
  createProvider,
  updateProvider,
  deleteProvider,
  toggleProviderEnabled,
  testProviderConnection,
  testNewProvider,
  fetchProviderModels,
  fetchProviderModelsWithConfig
} from '../api/provider';

export default {
  name: 'ProviderManage',
  components: {
    PlusOutlined
  },
  setup() {
    const loading = ref(false);
    const providerList = ref([]);
    const pagination = ref({
      current: 1,
      pageSize: 20,
      total: 0,
      showSizeChanger: false,
      showTotal: (total) => `共 ${total} 条`
    });

    const columns = [
      { title: '服务商', key: 'provider_name', width: 180 },
      { title: 'API类型', key: 'api_type', width: 130 },
      { title: 'API地址', key: 'api_url', ellipsis: true },
      { title: 'API Key', key: 'api_key', width: 100 },
      { title: '启用', key: 'is_enabled', width: 80 },
      { title: '操作', key: 'action', width: 180 }
    ];

    // 弹窗相关
    const modalVisible = ref(false);
    const isEdit = ref(false);
    const editingId = ref(null);
    const submitting = ref(false);
    const formRef = ref(null);
    
    const formData = reactive({
      provider_code: '',
      provider_name: '',
      api_type: 'openai_compatible',
      api_url: '',
      api_key: '',
      api_version: 'v1',
      default_model: '',
      test_model_id: '',
      extra_config: {
        project_id: '',
        region: 'us-central1',
        paygo_mode: false
      },
      is_enabled: true,
      sort_order: 0,
      description: ''
    });

    const formRules = {
      provider_code: [{ required: true, message: '请输入服务商代码' }],
      provider_name: [{ required: true, message: '请输入服务商名称' }],
      api_type: [{ required: true, message: '请选择API类型' }]
    };

    // 测试相关
    const testingId = ref(null);
    const testingNew = ref(false);
    const testResult = ref(null);
    const testResultModalVisible = ref(false);
    const testResultData = ref({});

    // 模型列表相关
    const availableModels = ref([]);
    const loadingModels = ref(false);

    // 计算是否可以测试
    const canTest = computed(() => {
      if (formData.api_type === 'openai_compatible') {
        return formData.api_url && formData.api_key && formData.test_model_id;
      } else if (formData.api_type === 'google_gemini') {
        if (formData.provider_code === 'vertex_ai') {
          return formData.extra_config?.project_id && formData.test_model_id;
        }
        return formData.api_key && formData.test_model_id;
      }
      return false;
    });

    // 加载列表
    const loadProviders = async () => {
      loading.value = true;
      try {
        const res = await getProviderList({
          page: pagination.value.current,
          pageSize: pagination.value.pageSize
        });
        if (res.code === 200) {
          // Response.pagination 返回的是 data.items，不是 data.list
          const items = res.data?.items || res.data?.list || (Array.isArray(res.data) ? res.data : []);
          providerList.value = items;
          pagination.value.total = res.data?.total || items.length;
        } else {
          providerList.value = [];
          pagination.value.total = 0;
        }
      } catch (error) {
        console.error('加载服务商列表失败:', error);
        message.error('加载失败');
        providerList.value = [];
        pagination.value.total = 0;
      } finally {
        loading.value = false;
      }
    };

    // 表格变化
    const handleTableChange = (pag) => {
      pagination.value.current = pag.current;
      loadProviders();
    };

    // 添加服务商
    const handleAddProvider = () => {
      isEdit.value = false;
      editingId.value = null;
      editingRecord.value = null;
      testResult.value = null;
      availableModels.value = []; // 清空模型列表
      Object.assign(formData, {
        provider_code: '',
        provider_name: '',
        api_type: 'openai_compatible',
        api_url: '',
        api_key: '',
        api_version: 'v1',
        default_model: '',
        test_model_id: '',
        extra_config: { project_id: '', region: 'us-central1', paygo_mode: false },
        is_enabled: true,
        sort_order: 0,
        description: ''
      });
      modalVisible.value = true;
    };

    // 编辑服务商
    const editingRecord = ref(null); // 保存编辑中的记录信息
    
    const handleEditProvider = async (record) => {
      isEdit.value = true;
      editingId.value = record.id;
      editingRecord.value = record; // 保存记录信息，用于显示 has_api_key
      testResult.value = null;
      availableModels.value = []; // 清空模型列表
      Object.assign(formData, {
        provider_code: record.provider_code,
        provider_name: record.provider_name,
        api_type: record.api_type,
        api_url: record.api_url || '',
        api_key: '', // 不显示原有密钥
        api_version: record.api_version || 'v1',
        default_model: record.default_model || '',
        test_model_id: record.test_model_id || '',
        extra_config: record.extra_config || { project_id: '', region: 'us-central1' },
        is_enabled: record.is_enabled === 1,
        sort_order: record.sort_order || 0,
        description: record.description || ''
      });
      modalVisible.value = true;
      
      // 如果已配置 API Key，自动获取模型列表
      if (record.has_api_key && record.api_type === 'openai_compatible') {
        try {
          loadingModels.value = true;
          const res = await fetchProviderModels(record.id);
          if (res.code === 200) {
            availableModels.value = res.data.models || [];
          }
        } catch (error) {
          console.error('自动获取模型列表失败:', error);
          // 静默失败，不影响编辑
        } finally {
          loadingModels.value = false;
        }
      }
    };

    // 提交表单
    const handleSubmit = async () => {
      try {
        await formRef.value.validate();
      } catch (e) {
        return;
      }

      submitting.value = true;
      try {
        const data = {
          ...formData,
          is_enabled: formData.is_enabled ? 1 : 0,
          extra_config: formData.api_type === 'google_gemini' ? formData.extra_config : null
        };

        // 如果是编辑且没有填写新密钥，不传 api_key
        if (isEdit.value && !data.api_key) {
          delete data.api_key;
        }

        let res;
        if (isEdit.value) {
          res = await updateProvider(editingId.value, data);
        } else {
          res = await createProvider(data);
        }

        if (res.code === 200) {
          message.success(isEdit.value ? '更新成功' : '创建成功');
          modalVisible.value = false;
          loadProviders();
        } else {
          message.error(res.message || '操作失败');
        }
      } catch (error) {
        console.error('提交失败:', error);
        message.error('操作失败');
      } finally {
        submitting.value = false;
      }
    };

    // 取消
    const handleCancel = () => {
      modalVisible.value = false;
    };

    // 删除服务商
    const handleDeleteProvider = async (id) => {
      try {
        const res = await deleteProvider(id);
        if (res.code === 200) {
          message.success('删除成功');
          loadProviders();
        } else {
          message.error(res.message || '删除失败');
        }
      } catch (error) {
        console.error('删除失败:', error);
        message.error('删除失败');
      }
    };

    // 切换启用状态
    const handleToggleEnabled = async (record, checked) => {
      try {
        const res = await toggleProviderEnabled(record.id);
        if (res.code === 200) {
          message.success(checked ? '已启用' : '已禁用');
          loadProviders();
        } else {
          message.error(res.message || '操作失败');
        }
      } catch (error) {
        console.error('切换状态失败:', error);
        message.error('操作失败');
      }
    };

    // 测试已保存的服务商
    const handleTestProvider = async (record) => {
      testingId.value = record.id;
      try {
        const res = await testProviderConnection(record.id);
        if (res.code === 200) {
          testResultData.value = res.data;
          testResultModalVisible.value = true;
        } else {
          message.error(res.message || '测试失败');
        }
      } catch (error) {
        console.error('测试失败:', error);
        message.error('测试失败: ' + (error.message || '未知错误'));
      } finally {
        testingId.value = null;
      }
    };

    // 测试新配置（未保存）
    const handleTestNewConfig = async () => {
      testingNew.value = true;
      testResult.value = null;
      try {
        const res = await testNewProvider({
          api_type: formData.api_type,
          api_url: formData.api_url,
          api_key: formData.api_key,
          api_version: formData.api_version,
          test_model_id: formData.test_model_id,
          extra_config: formData.extra_config
        });
        if (res.code === 200) {
          testResult.value = { success: true, duration_ms: res.data.duration_ms };
          message.success('测试成功');
        } else {
          testResult.value = { success: false };
          message.error(res.message || '测试失败');
        }
      } catch (error) {
        testResult.value = { success: false };
        console.error('测试失败:', error);
        message.error('测试失败: ' + (error.message || '未知错误'));
      } finally {
        testingNew.value = false;
      }
    };

    // 获取模型列表
    const handleFetchModels = async () => {
      // 检查必要配置
      if (!formData.api_url || !formData.api_key) {
        message.warning('请先填写 API 地址和 API Key');
        return;
      }
      
      loadingModels.value = true;
      try {
        let res;
        
        // 如果是编辑模式且已保存的服务商，使用服务商ID获取
        if (isEdit.value && editingId.value) {
          res = await fetchProviderModels(editingId.value);
        } else {
          // 新建模式，使用临时配置获取
          res = await fetchProviderModelsWithConfig({
            api_type: formData.api_type,
            api_url: formData.api_url,
            api_key: formData.api_key,
            api_version: formData.api_version,
            extra_config: formData.extra_config
          });
        }
        
        if (res.code === 200) {
          availableModels.value = res.data.models || [];
          message.success(`成功获取 ${availableModels.value.length} 个模型`);
        } else {
          message.error(res.message || '获取模型列表失败');
        }
      } catch (error) {
        console.error('获取模型列表失败:', error);
        message.error('获取模型列表失败: ' + (error.message || '未知错误'));
      } finally {
        loadingModels.value = false;
      }
    };

    // 模型下拉框搜索过滤
    const filterModelOption = (input, option) => {
      const value = option.value || '';
      const label = option.children?.[0]?.children || '';
      const searchText = (input || '').toLowerCase();
      return (
        value.toLowerCase().includes(searchText) ||
        (typeof label === 'string' && label.toLowerCase().includes(searchText))
      );
    };

    // 辅助函数
    const getApiTypeName = (type) => {
      const map = {
        'openai_compatible': 'OpenAI 兼容',
        'google_gemini': 'Google Gemini',
        'custom': '自定义'
      };
      return map[type] || type;
    };

    const getApiTypeColor = (type) => {
      const map = {
        'openai_compatible': 'blue',
        'google_gemini': 'green',
        'custom': 'orange'
      };
      return map[type] || 'default';
    };

    onMounted(() => {
      loadProviders();
    });

    return {
      loading,
      providerList,
      pagination,
      columns,
      modalVisible,
      isEdit,
      submitting,
      formRef,
      formData,
      formRules,
      testingId,
      testingNew,
      testResult,
      testResultModalVisible,
      testResultData,
      canTest,
      handleTableChange,
      handleAddProvider,
      handleEditProvider,
      handleSubmit,
      handleCancel,
      handleDeleteProvider,
      handleToggleEnabled,
      handleTestProvider,
      handleTestNewConfig,
      getApiTypeName,
      getApiTypeColor,
      editingRecord,
      availableModels,
      loadingModels,
      handleFetchModels,
      filterModelOption
    };
  }
};
</script>
